import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Button, Select, Text } from "@mantine/core"
import {
  IconBolt,
  IconLanguage,
  IconLock,
  IconMicrophone,
  IconMicrophoneOff,
  IconPlayerRecordFilled,
  IconShieldCheck,
  IconSparkles,
  IconWaveSquare,
} from "@tabler/icons-react"
import { useEffect, useMemo, useRef } from "react"
import { IS_TAURI } from "../../lib/runtime-origin"
import { micHelpEnvironment, type TMicHelpEnvironment } from "./blocked-mic-help"
import type { IAudioPipelineHandle, TMicLevel, TPermissionState } from "./interfaces"
import classes from "./PreRecord.module.css"
import { Topbar } from "./Topbar"
import { Visualizer } from "./Visualizer"

export type TPreRecordMode = "full" | "lite"
export type TPreRecordTranscription = "live" | "batch"

interface IProps {
  subject: string
  startTime: string | null
  permission: TPermissionState
  /** Re-runs the microphone request after the user changes the blocked permission. */
  onRecheckPermission: () => void
  /**
   * Permission "prompt" only. Must call getUserMedia inside the click so the
   * browser treats it as a user gesture. Must not start recording.
   */
  onAllowMicrophone: () => void
  /** Starts recording. Used once permission is already granted (or unknown). */
  onStart: () => void
  /** Optional rename handler — wired to the topbar pencil. */
  onRename?: () => void
  /** Languages preselected (or auto-detect) from the new meeting dialog. */
  languages: string[]
  autoDetect: boolean
  /** Full or Lite. The chip people actually recognise. */
  mode: TPreRecordMode
  /** Live transcript vs batch. Drives the privacy footnote. */
  transcription: TPreRecordTranscription
  /** Mode label, e.g. "Live transcript" or "Batch summary". */
  modeLabel: string
  /** Story override. Production detects desktop / Safari / Chrome / fallback. */
  helpEnvironment?: TMicHelpEnvironment
  audio: IAudioPipelineHandle
  /**
   * Recoverable error message surfaced before recording starts (e.g. a failed
   * start attempt, expired session, or unsupported browser). null/empty = no
   * banner. Rendered as a dismissible danger Alert above the hero.
   */
  error?: string | null
  /** Dismisses the error banner. */
  onDismissError?: () => void
}

const SILENT_THRESHOLD = 0.025
const CLIPPING_THRESHOLD = 0.251

function computeLevel(volume: number, monitoring: boolean): TMicLevel {
  if (!monitoring) return "silent"
  if (volume >= CLIPPING_THRESHOLD) return "clipping"
  if (volume <= SILENT_THRESHOLD) return "silent"
  return "normal"
}

const LANGUAGE_LABEL: Record<string, string> = {
  nl: "Dutch",
  en: "English",
  de: "German",
  fr: "French",
  es: "Spanish",
  it: "Italian",
  pt: "Portuguese",
  pl: "Polish",
  sv: "Swedish",
  da: "Danish",
  no: "Norwegian",
  fi: "Finnish",
  ja: "Japanese",
  zh: "Mandarin",
  ar: "Arabic",
}

function BlockedMicHelp({ environment }: { environment: TMicHelpEnvironment }): React.JSX.Element {
  switch (environment) {
    case "desktop":
      return <Trans>Allow the microphone for this app in your system privacy settings, then press Check again.</Trans>
    case "safari":
      return <Trans>In Safari, open Settings → Websites → Microphone, allow this site, then press Check again.</Trans>
    case "chromium":
      return <Trans>Click the lock icon in the address bar, set the microphone to Allow, then press Check again.</Trans>
    default:
      return <Trans>Allow the microphone for this site in your browser settings, then press Check again.</Trans>
  }
}

function PrivacyLine({ transcription }: { transcription: TPreRecordTranscription }): React.JSX.Element {
  if (transcription === "batch") {
    return (
      <Trans>
        Your audio stays on this device until you finish, then goes to our transcription service. No bot joins your
        call.
      </Trans>
    )
  }
  return (
    <Trans>
      While you record, your audio goes to our transcription service so you can follow along live. No bot joins your
      call.
    </Trans>
  )
}

export function PreRecord({
  subject,
  startTime,
  permission,
  onRecheckPermission,
  onAllowMicrophone,
  onStart,
  onRename,
  languages,
  autoDetect,
  mode,
  transcription,
  modeLabel,
  helpEnvironment,
  audio,
  error = null,
  onDismissError,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  // `prompt` stays until the Permissions API notices the click. Once monitoring
  // is live the mic is usable, so the button can switch to Start recording.
  const micLive = audio.monitoring
  const granted = permission === "granted" || permission === "unknown" || (permission === "prompt" && micLive)
  const prompting = permission === "prompt" && !micLive
  const denied = permission === "denied"

  // Kick off live monitoring once permission is granted so the visualizer
  // shows real volume. Only fire when monitoring isn't already active.
  const startedMonitoringRef = useRef(false)
  useEffect(() => {
    if (!granted) return
    if (audio.monitoring) {
      startedMonitoringRef.current = true
      return
    }
    if (startedMonitoringRef.current) return
    if (!audio.selectedDevice) return
    startedMonitoringRef.current = true
    audio.startMonitoring(audio.selectedDevice).catch(() => {
      startedMonitoringRef.current = false
    })
  }, [granted, audio])

  const level: TMicLevel = useMemo(
    () => (granted ? computeLevel(audio.volume, audio.monitoring) : "silent"),
    [granted, audio.volume, audio.monitoring],
  )

  const selectedDeviceLabel = useMemo(() => {
    const dev = audio.devices.find((d) => d.deviceId === audio.selectedDevice)
    return dev?.label || i18n._(msg`Default microphone`)
  }, [audio.devices, audio.selectedDevice, i18n])

  const ctaSubLine = useMemo(() => {
    if (denied) return i18n._(msg`Grant permission below to continue`)
    if (prompting) return i18n._(msg`Browser will ask for permission`)
    return i18n._(msg`Mic is ready · ${selectedDeviceLabel}`)
  }, [denied, prompting, selectedDeviceLabel, i18n])

  const ctaLabel = useMemo(() => {
    if (denied) return i18n._(msg`Microphone blocked`)
    if (prompting) return i18n._(msg`Allow microphone`)
    return i18n._(msg`Start recording`)
  }, [denied, prompting, i18n])

  const blockedHelp = useMemo(
    () =>
      helpEnvironment ??
      micHelpEnvironment({
        isDesktop: IS_TAURI,
        userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
      }),
    [helpEnvironment],
  )

  const languagesLabel = useMemo(() => {
    if (autoDetect) return i18n._(msg`Auto-detect`)
    if (languages.length === 0) return i18n._(msg`Auto-detect`)
    return languages.map((c) => LANGUAGE_LABEL[c] ?? c).join(" · ")
  }, [autoDetect, languages, i18n])

  const ctaIcon = denied ? (
    <IconMicrophoneOff size={20} />
  ) : prompting ? (
    <IconMicrophone size={20} />
  ) : (
    <IconPlayerRecordFilled size={20} />
  )

  // Keyboard ⌘/Ctrl + Enter starts recording when granted.
  useEffect(() => {
    if (!granted) return
    const handler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault()
        onStart()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [granted, onStart])

  return (
    <div className={classes.shell} data-testid="recording-prerecord">
      <Topbar phase="prerecord" subject={subject} startTime={startTime} onRename={onRename} />

      <div className={classes.grid}>
        <section className={classes.hero}>
          {error ? (
            <Alert
              color="danger"
              variant="light"
              title={<Trans>Couldn&apos;t start recording</Trans>}
              withCloseButton={Boolean(onDismissError)}
              onClose={onDismissError}
              mb="md"
              data-testid="prerecord-error"
            >
              <Text size="sm">{error}</Text>
            </Alert>
          ) : null}

          <div className={classes.eyebrow}>
            <IconWaveSquare size={14} />
            <Trans>New meeting</Trans>
          </div>

          <h1 className={classes.headline}>
            <Trans>
              Ready when <span className={classes.headlineGradient}>you are.</span>
            </Trans>
          </h1>

          <p className={classes.body}>
            <Trans>Put your laptop or phone where everyone can be heard.</Trans>
          </p>

          <div className={classes.configRow}>
            <span className={classes.cfgChip}>
              <IconLanguage size={13} />
              <Trans>Languages</Trans> <strong>{languagesLabel}</strong>
            </span>
            <span className={classes.cfgChip} data-testid="prerecord-mode">
              <IconSparkles size={13} />
              <strong>{mode === "lite" ? <Trans>Lite</Trans> : <Trans>Full</Trans>}</strong>
            </span>
            <span className={classes.cfgChip}>
              <IconBolt size={13} />
              <strong>{modeLabel}</strong>
            </span>
          </div>

          <button
            type="button"
            className={classes.startBtn}
            onClick={prompting ? onAllowMicrophone : onStart}
            disabled={denied}
            data-testid="recording-start"
          >
            <span className={classes.startMic}>{ctaIcon}</span>
            <span className={classes.startBody}>
              {ctaLabel}
              <span className={classes.startSub}>{ctaSubLine}</span>
            </span>
          </button>

          {denied && (
            <div className={`${classes.perm} ${classes.permDenied}`} role="alert">
              <span className={classes.permIcon}>
                <IconLock size={14} />
              </span>
              <div className={classes.permBody}>
                <div className={classes.permTitle}>
                  <Trans>Microphone access is blocked</Trans>
                </div>
                <div className={classes.permSub}>
                  <BlockedMicHelp environment={blockedHelp} />
                </div>
              </div>
              <Button
                variant="default"
                size="xs"
                className={classes.recheck}
                onClick={onRecheckPermission}
                data-testid="recording-recheck"
              >
                <Trans>Check again</Trans>
              </Button>
            </div>
          )}
        </section>

        <aside className={classes.checkCard}>
          <div className={classes.checkHead}>
            <span className={classes.checkTitle}>
              <IconMicrophone size={16} color="var(--mantine-color-brand-5)" />
              <Trans>Microphone check</Trans>
            </span>
            <span className={`${classes.liveIndicator} ${audio.monitoring ? classes.liveActive : ""}`}>
              <span className={audio.monitoring ? classes.liveDotActive : classes.liveDotIdle} />
              {audio.monitoring ? <Trans>Live</Trans> : <Trans>Idle</Trans>}
            </span>
          </div>

          <Visualizer level={level} volume={audio.volume} spectrum={audio.spectrum} monitoring={audio.monitoring} />

          <div className={classes.deviceRow}>
            <Select
              aria-label={i18n._(msg`Microphone`)}
              data={audio.devices.map((d) => ({
                value: d.deviceId,
                label: d.label || i18n._(msg`Microphone`),
              }))}
              value={audio.selectedDevice}
              onChange={(v) => v && audio.setSelectedDevice(v)}
              size="sm"
              disabled={audio.devices.length === 0}
              placeholder={i18n._(msg`Select microphone`)}
            />
          </div>

          <div className={classes.toggleRow}>
            <button
              type="button"
              className={classes.toggle}
              data-on={audio.boostEnabled ? "true" : "false"}
              aria-pressed={audio.boostEnabled}
              onClick={() => audio.handleBoostToggle(!audio.boostEnabled)}
            >
              <span className={classes.toggleDot} />
              <Trans>Boost</Trans>
            </button>
          </div>

          <span className={classes.footnote} data-testid="prerecord-privacy">
            <span className={classes.footnoteIcon}>
              <IconShieldCheck size={13} />
            </span>
            <PrivacyLine transcription={transcription} />
          </span>
        </aside>
      </div>
    </div>
  )
}
